/**
 * Overlay style definitions — 5 visual themes for the OBS overlay.
 * Same data, same layout positions, different visual treatment.
 */

import type { OverlayStyle } from "@/types";

export interface OverlayTheme {
  id: OverlayStyle;
  name: string;
  description: string;

  // Heat banner (top-left)
  banner: {
    bg: string;
    border: string;
    text: string;
    subtext: string;
    liveColor: string;
  };

  // Rider chips (bottom bar)
  chip: {
    bg: string;
    border: string;
    nameColor: string;
    scoreColor: string;
    hitsColor: string;
    avatarBorder: string;
  };

  // Focus card (left side)
  focus: {
    bg: string;
    border: string;
    nameColor: string;
    nickColor: string;
    metaColor: string;
    factColor: string;
  };

  // Scorecard (center)
  scorecard: {
    bg: string;
    border: string;
    nameColor: string;
    judgeLabelColor: string;
    scoreColor: string;
    finalColor: string;
    finalLabelColor: string;
    divider: string;
  };

  // Leaderboard (center)
  leaderboard: {
    bg: string;
    border: string;
    headerBg: string;
    headerColor: string;
    rowBg: string;
    rowAltBg: string;
    rowBorder: string;
    nameColor: string;
    scoreColor: string;
    firstPlaceColor: string;
    finalColor: string;
    titleColor: string;
  };

  // Backdrop filter
  blur: string;
}

export const OVERLAY_STYLES: Record<OverlayStyle, OverlayTheme> = {
  steel: {
    id: "steel",
    name: "Steel",
    description: "Dark, minimal, sharp edges. The default.",
    banner: { bg: "rgba(10,10,11,0.88)", border: "rgba(255,255,255,0.08)", text: "#fff", subtext: "#52525b", liveColor: "#ef4444" },
    chip: { bg: "rgba(10,10,11,0.85)", border: "rgba(255,255,255,0.08)", nameColor: "#fff", scoreColor: "#94a3b8", hitsColor: "#52525b", avatarBorder: "rgba(255,255,255,0.15)" },
    focus: { bg: "rgba(10,10,11,0.92)", border: "rgba(255,255,255,0.08)", nameColor: "#fff", nickColor: "#94a3b8", metaColor: "#64748b", factColor: "#64748b" },
    scorecard: { bg: "rgba(10,10,11,0.92)", border: "rgba(255,255,255,0.08)", nameColor: "#fff", judgeLabelColor: "#64748b", scoreColor: "#fff", finalColor: "#3b82f6", finalLabelColor: "#64748b", divider: "rgba(255,255,255,0.08)" },
    leaderboard: { bg: "rgba(10,10,11,0.92)", border: "rgba(255,255,255,0.08)", headerBg: "transparent", headerColor: "#52525b", rowBg: "transparent", rowAltBg: "rgba(255,255,255,0.02)", rowBorder: "rgba(255,255,255,0.04)", nameColor: "#fff", scoreColor: "#d4d4d8", firstPlaceColor: "#eab308", finalColor: "#3b82f6", titleColor: "#fff" },
    blur: "blur(8px)",
  },

  olympic: {
    id: "olympic",
    name: "Olympic",
    description: "Blue gradient rows, bold headers. Inspired by IWWF broadcast.",
    banner: { bg: "rgba(0,40,80,0.92)", border: "rgba(100,180,255,0.2)", text: "#fff", subtext: "rgba(180,220,255,0.6)", liveColor: "#ef4444" },
    chip: { bg: "rgba(0,40,80,0.88)", border: "rgba(100,180,255,0.15)", nameColor: "#fff", scoreColor: "#93c5fd", hitsColor: "rgba(147,197,253,0.5)", avatarBorder: "rgba(100,180,255,0.3)" },
    focus: { bg: "rgba(0,30,70,0.94)", border: "rgba(100,180,255,0.2)", nameColor: "#fff", nickColor: "#93c5fd", metaColor: "rgba(147,197,253,0.6)", factColor: "rgba(147,197,253,0.5)" },
    scorecard: { bg: "rgba(0,30,70,0.94)", border: "rgba(100,180,255,0.2)", nameColor: "#fff", judgeLabelColor: "rgba(147,197,253,0.6)", scoreColor: "#fff", finalColor: "#fbbf24", finalLabelColor: "rgba(147,197,253,0.6)", divider: "rgba(100,180,255,0.15)" },
    leaderboard: { bg: "rgba(0,30,70,0.94)", border: "rgba(100,180,255,0.2)", headerBg: "rgba(0,50,110,0.8)", headerColor: "rgba(180,220,255,0.8)", rowBg: "rgba(0,50,110,0.3)", rowAltBg: "rgba(0,60,130,0.4)", rowBorder: "rgba(100,180,255,0.08)", nameColor: "#fff", scoreColor: "#bfdbfe", firstPlaceColor: "#fbbf24", finalColor: "#fbbf24", titleColor: "#fff" },
    blur: "blur(10px)",
  },

  broadcast: {
    id: "broadcast",
    name: "Broadcast",
    description: "Bold ESPN/EPL style. High contrast, ticker-tape energy.",
    banner: { bg: "rgba(220,38,38,0.95)", border: "rgba(255,255,255,0.2)", text: "#fff", subtext: "rgba(255,255,255,0.7)", liveColor: "#fff" },
    chip: { bg: "rgba(15,23,42,0.95)", border: "rgba(255,255,255,0.1)", nameColor: "#fff", scoreColor: "#fbbf24", hitsColor: "rgba(251,191,36,0.5)", avatarBorder: "rgba(251,191,36,0.4)" },
    focus: { bg: "rgba(15,23,42,0.96)", border: "rgba(220,38,38,0.4)", nameColor: "#fff", nickColor: "#fbbf24", metaColor: "rgba(255,255,255,0.5)", factColor: "rgba(255,255,255,0.4)" },
    scorecard: { bg: "rgba(15,23,42,0.96)", border: "rgba(220,38,38,0.3)", nameColor: "#fff", judgeLabelColor: "rgba(255,255,255,0.5)", scoreColor: "#fff", finalColor: "#fbbf24", finalLabelColor: "rgba(255,255,255,0.5)", divider: "rgba(220,38,38,0.3)" },
    leaderboard: { bg: "rgba(15,23,42,0.96)", border: "rgba(220,38,38,0.3)", headerBg: "rgba(220,38,38,0.9)", headerColor: "#fff", rowBg: "rgba(30,41,59,0.8)", rowAltBg: "rgba(30,41,59,0.6)", rowBorder: "rgba(255,255,255,0.05)", nameColor: "#fff", scoreColor: "#e2e8f0", firstPlaceColor: "#fbbf24", finalColor: "#fbbf24", titleColor: "#fff" },
    blur: "blur(6px)",
  },

  neon: {
    id: "neon",
    name: "Neon",
    description: "Dark with glowing accents. Cyberpunk energy.",
    banner: { bg: "rgba(0,0,0,0.9)", border: "rgba(34,211,238,0.3)", text: "#22d3ee", subtext: "rgba(34,211,238,0.4)", liveColor: "#f43f5e" },
    chip: { bg: "rgba(0,0,0,0.88)", border: "rgba(34,211,238,0.2)", nameColor: "#e0f2fe", scoreColor: "#22d3ee", hitsColor: "rgba(34,211,238,0.4)", avatarBorder: "rgba(34,211,238,0.4)" },
    focus: { bg: "rgba(0,0,0,0.94)", border: "rgba(34,211,238,0.25)", nameColor: "#e0f2fe", nickColor: "#22d3ee", metaColor: "rgba(34,211,238,0.5)", factColor: "rgba(34,211,238,0.4)" },
    scorecard: { bg: "rgba(0,0,0,0.94)", border: "rgba(34,211,238,0.25)", nameColor: "#e0f2fe", judgeLabelColor: "rgba(34,211,238,0.5)", scoreColor: "#e0f2fe", finalColor: "#22d3ee", finalLabelColor: "rgba(34,211,238,0.5)", divider: "rgba(34,211,238,0.15)" },
    leaderboard: { bg: "rgba(0,0,0,0.94)", border: "rgba(34,211,238,0.25)", headerBg: "rgba(34,211,238,0.1)", headerColor: "#22d3ee", rowBg: "transparent", rowAltBg: "rgba(34,211,238,0.04)", rowBorder: "rgba(34,211,238,0.08)", nameColor: "#e0f2fe", scoreColor: "rgba(224,242,254,0.7)", firstPlaceColor: "#f43f5e", finalColor: "#22d3ee", titleColor: "#22d3ee" },
    blur: "blur(12px)",
  },

  clean: {
    id: "clean",
    name: "Clean",
    description: "Light, minimal, modern. Apple-esque.",
    banner: { bg: "rgba(255,255,255,0.92)", border: "rgba(0,0,0,0.08)", text: "#0f172a", subtext: "#94a3b8", liveColor: "#ef4444" },
    chip: { bg: "rgba(255,255,255,0.9)", border: "rgba(0,0,0,0.06)", nameColor: "#0f172a", scoreColor: "#475569", hitsColor: "#94a3b8", avatarBorder: "rgba(0,0,0,0.1)" },
    focus: { bg: "rgba(255,255,255,0.95)", border: "rgba(0,0,0,0.08)", nameColor: "#0f172a", nickColor: "#475569", metaColor: "#94a3b8", factColor: "#94a3b8" },
    scorecard: { bg: "rgba(255,255,255,0.95)", border: "rgba(0,0,0,0.08)", nameColor: "#0f172a", judgeLabelColor: "#94a3b8", scoreColor: "#0f172a", finalColor: "#2563eb", finalLabelColor: "#94a3b8", divider: "rgba(0,0,0,0.06)" },
    leaderboard: { bg: "rgba(255,255,255,0.95)", border: "rgba(0,0,0,0.08)", headerBg: "rgba(0,0,0,0.03)", headerColor: "#64748b", rowBg: "transparent", rowAltBg: "rgba(0,0,0,0.02)", rowBorder: "rgba(0,0,0,0.04)", nameColor: "#0f172a", scoreColor: "#334155", firstPlaceColor: "#d97706", finalColor: "#2563eb", titleColor: "#0f172a" },
    blur: "blur(12px)",
  },
};

export const STYLE_LIST = Object.values(OVERLAY_STYLES);
